import { useState, useEffect, useRef } from "react";
import { getSceneDesign, saveSceneDesign, lockSceneDesign, generateCostume } from "@/services/sceneService";
import { get3DDesign } from "@/services/locationService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Users, MapPin, Loader2, Lock, Unlock, Shirt, Plus, Camera,
  Sun, Moon, Sunset, CloudSun, Image, Upload, Sparkles, Package,
  ChevronDown, Eye, Download
} from "lucide-react";


const LIGHTING_PRESETS = [
  { id: "day", label: "Day", icon: Sun, desc: "Bright natural light", color: "text-yellow-400" },
  { id: "night", label: "Night", icon: Moon, desc: "Dark, moonlit or artificial", color: "text-blue-400" },
  { id: "evening", label: "Evening", icon: Sunset, desc: "Warm sunset tones", color: "text-orange-400" },
  { id: "golden-hour", label: "Golden Hour", icon: CloudSun, desc: "Soft warm golden light", color: "text-amber-400" },
  { id: "overcast", label: "Overcast", icon: CloudSun, desc: "Diffused, soft shadows", color: "text-gray-400" },
];

export default function EnhancedSceneDesignTab({ scene, characters, locations = [], projectId, onUpdate }) {
  const [design, setDesign] = useState({
    location_id: null,
    lighting_preset: "day",
    lighting_overrides: {},
    character_costumes: {},
    additional_props: [],
    is_locked: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [generatingCostume, setGeneratingCostume] = useState(null);
  const [costumePrompt, setCostumePrompt] = useState("");
  const [editingCostumeFor, setEditingCostumeFor] = useState(null);
  const [showLightbox, setShowLightbox] = useState(null);
  const [locationDesign, setLocationDesign] = useState(null);
  const costumeFileRef = useRef(null);

  const sceneCharacters = characters.filter(c =>
    scene.characters?.some(sc => sc.toLowerCase() === c.name.toLowerCase())
  );

  // Auto-match location
  const matchedLocation = locations.find(l =>
    scene.location?.toLowerCase().includes(l.name.toLowerCase()) ||
    l.name.toLowerCase().includes(scene.location?.toLowerCase() || '')
  );

  // Load scene design
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getSceneDesign(projectId, scene.number);
        if (data) {
          setDesign(prev => ({ ...prev, ...data }));
          if (data.location_id) loadLocationDesign(data.location_id);
        } else if (matchedLocation) {
          setDesign(prev => ({ ...prev, location_id: matchedLocation.id }));
          loadLocationDesign(matchedLocation.id);
        }
      } catch { /* no design yet */ }
      finally { setLoading(false); }
    };
    load();
  }, [projectId, scene.number]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLocationDesign = async (locId) => {
    try {
      const data = await get3DDesign(locId);
      setLocationDesign(data);
    } catch { setLocationDesign(null); }
  };

  const handleImportLocation = async (locId) => {
    setDesign(prev => ({ ...prev, location_id: locId }));
    await loadLocationDesign(locId);
    toast.success("Location imported into scene");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSceneDesign({
        ...design, project_id: projectId, scene_number: scene.number,
      });
      toast.success("Scene design saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleLock = async () => {
    if (!design.is_locked) {
      setLocking(true);
      try {
        await lockSceneDesign({
          project_id: projectId, scene_number: scene.number,
        });
        setDesign(prev => ({ ...prev, is_locked: true }));
        toast.success("Scene design locked");
        onUpdate?.();
      } catch (err) { toast.error(err.response?.data?.detail || "Failed to lock"); }
      finally { setLocking(false); }
    } else {
      setDesign(prev => ({ ...prev, is_locked: false }));
      toast.success("Scene design unlocked");
    }
  };

  const handleGenerateCostume = async (charId, refImage) => {
    if (!costumePrompt.trim() && !refImage) return toast.error("Describe the costume or upload a reference");
    setGeneratingCostume(charId);
    try {
      const data = await generateCostume({
        character_id: charId,
        scene_number: scene.number,
        project_id: projectId,
        costume_prompt: costumePrompt,
        reference_image: refImage || null,
      });
      setDesign(prev => ({
        ...prev,
        character_costumes: {
          ...prev.character_costumes,
          [charId]: { prompt: costumePrompt, image: data.image_data, generated_at: new Date().toISOString() }
        }
      }));
      toast.success("Costume generated!");
      setEditingCostumeFor(null);
      setCostumePrompt("");
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Costume generation failed");
    } finally { setGeneratingCostume(null); }
  };

  const handleCostumeRefUpload = (charId) => {
    const input = costumeFileRef.current;
    if (!input) return;
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result.split(",")[1];
        handleGenerateCostume(charId, base64);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const selectedLocation = locations.find(l => l.id === design.location_id);

  if (loading) {
    return <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[var(--color-gold-400)]" size={24} /></div>;
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-5 space-y-6 max-w-3xl" data-testid="enhanced-scene-design">
        <input ref={costumeFileRef} type="file" accept="image/*" className="hidden" />

        {/* Lock indicator */}
        {design.is_locked && (
          <div className="p-3 bg-[rgba(212,168,83,0.1)] border border-[rgba(212,168,83,0.3)] rounded-xl flex items-center gap-2">
            <Lock size={14} className="text-[var(--color-gold-400)]" />
            <span className="text-xs text-[var(--color-gold-400)] font-medium">Scene design is locked — location, lighting, and costumes are finalized</span>
            <Button onClick={handleLock} className="btn-ghost text-xs ml-auto h-7 px-2" size="sm">
              <Unlock size={12} className="mr-1" />Unlock
            </Button>
          </div>
        )}

        {/* ═══ LOCATION ═══ */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-3">
            <MapPin size={15} className="text-[var(--color-gold-400)]" />
            Location
          </h3>
          {/* Import dropdown */}
          <div className="mb-3">
            <Select value={design.location_id || ""} onValueChange={handleImportLocation} disabled={design.is_locked}>
              <SelectTrigger className="input h-9 text-xs" data-testid="import-location-select">
                <SelectValue placeholder="Import a location..." />
              </SelectTrigger>
              <SelectContent>
                {locations.map(l => (
                  <SelectItem key={l.id} value={l.id} className="text-xs">
                    <span className="flex items-center gap-2">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                        l.location_type === "EXT" ? "bg-sky-500/20 text-sky-400" : "bg-amber-500/20 text-amber-400"
                      }`}>{l.location_type || "INT"}</span>
                      {l.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location preview with renders */}
          {selectedLocation && (
            <div className="p-4 bg-[var(--bg-overlay)] rounded-xl border border-[var(--border-subtle)]">
              <div className="flex items-start gap-4 mb-3">
                {selectedLocation.reference_image ? (
                  <img src={`data:image/png;base64,${selectedLocation.reference_image}`} alt={selectedLocation.name}
                    className="w-24 h-16 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-24 h-16 rounded-lg bg-[var(--bg-muted)] flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-[var(--text-disabled)]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm text-[var(--text-primary)]">{selectedLocation.name}</h4>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                      selectedLocation.location_type === "EXT" ? "bg-sky-500/20 text-sky-400" : "bg-amber-500/20 text-amber-400"
                    }`}>{selectedLocation.location_type || "INT"}</span>
                    {locationDesign?.is_locked && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-0.5">
                        <Lock size={8} />Designed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-muted)] line-clamp-2">{selectedLocation.description}</p>
                </div>
              </div>
              {/* Location design renders */}
              {locationDesign?.design_renders?.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2">Location Design References</p>
                  <div className="flex gap-2 overflow-x-auto">
                    {locationDesign.design_renders.map((r, i) => (
                      <div key={r.angle || i} className="flex-shrink-0 w-28 cursor-pointer group"
                        onClick={() => setShowLightbox(r)}>
                        <img src={`data:image/png;base64,${r.image_data}`} alt={r.label}
                          className="w-full h-16 object-cover rounded-lg border border-[var(--border-subtle)] group-hover:border-[rgba(212,168,83,0.5)] transition-all" />
                        <span className="text-[9px] text-[var(--text-disabled)] mt-0.5 block">{r.label || r.angle}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!locationDesign?.design_renders?.length && (
                <p className="text-[10px] text-[var(--text-disabled)] italic">No 3D design renders yet — go to Locations &gt; 3D Design to create them</p>
              )}
            </div>
          )}
        </div>

        {/* ═══ LIGHTING ═══ */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-3">
            <Sun size={15} className="text-[var(--color-gold-400)]" />
            Scene Lighting
          </h3>
          <div className="grid grid-cols-5 gap-2">
            {LIGHTING_PRESETS.map(lp => (
              <button key={lp.id}
                onClick={() => !design.is_locked && setDesign(prev => ({ ...prev, lighting_preset: lp.id }))}
                disabled={design.is_locked}
                className={`p-3 rounded-xl border text-center transition-all disabled:opacity-50 ${
                  design.lighting_preset === lp.id
                    ? "border-[rgba(212,168,83,0.5)] bg-[rgba(212,168,83,0.1)]"
                    : "border-[var(--border-subtle)] bg-[var(--bg-overlay)] hover:border-[var(--border-default)]"
                }`}
                data-testid={`lighting-${lp.id}`}
              >
                <lp.icon size={18} className={`mx-auto mb-1 ${design.lighting_preset === lp.id ? lp.color : "text-[var(--text-muted)]"}`} />
                <span className={`text-[10px] font-medium block ${design.lighting_preset === lp.id ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"}`}>{lp.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ═══ CHARACTERS & COSTUMES ═══ */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-3">
            <Users size={15} className="text-[var(--color-gold-400)]" />
            Characters & Costumes
          </h3>
          {sceneCharacters.length === 0 ? (
            <div className="text-center py-6 bg-[var(--bg-overlay)] rounded-xl border border-[var(--border-subtle)]">
              <Users size={24} className="mx-auto text-[var(--text-disabled)] mb-2" />
              <p className="text-xs text-[var(--text-muted)]">No characters detected in this scene</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sceneCharacters.map(char => {
                const costume = design.character_costumes?.[char.id];
                const existingSheet = char.scene_character_sheets?.[`scene_${scene.number}`];
                const costumeImage = costume?.image || existingSheet;
                const isEditing = editingCostumeFor === char.id;
                const isGenerating = generatingCostume === char.id;

                return (
                  <div key={char.id} className="p-4 bg-[var(--bg-overlay)] rounded-xl border border-[var(--border-subtle)]">
                    <div className="flex items-start gap-3">
                      {/* Character face + costume preview */}
                      <div className="flex gap-2 flex-shrink-0">
                        {char.default_character_sheet_photo && (
                          <img src={`data:image/png;base64,${char.default_character_sheet_photo}`} alt={char.name}
                            className="w-14 h-14 rounded-lg object-cover border border-[var(--border-subtle)]" />
                        )}
                        {costumeImage && (
                          <img src={`data:image/png;base64,${costumeImage}`} alt={`${char.name} costume`}
                            className="w-14 h-14 rounded-lg object-cover border border-[rgba(212,168,83,0.3)] cursor-pointer"
                            onClick={() => setShowLightbox({ image_data: costumeImage, label: `${char.name} — Costume` })} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium text-[var(--text-primary)]">{char.name}</h4>
                          {costumeImage && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 flex items-center gap-0.5">
                              <Shirt size={8} />Costume Set
                            </span>
                          )}
                        </div>
                        {costume?.prompt && (
                          <p className="text-[10px] text-[var(--text-muted)] mb-1 italic">{costume.prompt}</p>
                        )}

                        {/* Costume actions */}
                        {!design.is_locked && (
                          <>
                            {isEditing ? (
                              <div className="space-y-2 mt-2">
                                <Textarea
                                  value={costumePrompt}
                                  onChange={(e) => setCostumePrompt(e.target.value)}
                                  placeholder="Describe the costume (e.g. 'White cotton kurta with beige dhoti, gold watch')..."
                                  className="input text-xs resize-none h-16"
                                  data-testid={`costume-prompt-${char.id}`}
                                />
                                <div className="flex gap-2">
                                  <Button onClick={() => handleGenerateCostume(char.id)}
                                    disabled={isGenerating || !costumePrompt.trim()}
                                    className="btn-primary text-xs h-7 flex-1" size="sm">
                                    {isGenerating ? <Loader2 size={11} className="animate-spin mr-1" /> : <Sparkles size={11} className="mr-1" />}
                                    Generate from Prompt
                                  </Button>
                                  <Button onClick={() => handleCostumeRefUpload(char.id)}
                                    disabled={isGenerating}
                                    className="btn-secondary text-xs h-7" size="sm">
                                    <Upload size={11} className="mr-1" />Ref Image
                                  </Button>
                                  <Button onClick={() => { setEditingCostumeFor(null); setCostumePrompt(""); }}
                                    className="btn-ghost text-xs h-7" size="sm">Cancel</Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                onClick={() => { setEditingCostumeFor(char.id); setCostumePrompt(costume?.prompt || ""); }}
                                className="btn-secondary text-xs h-7 mt-1" size="sm">
                                <Shirt size={11} className="mr-1" />{costumeImage ? "Change Costume" : "Design Costume"}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ═══ PROPS ═══ */}
        {scene.props?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-3">
              <Package size={15} className="text-[var(--color-gold-400)]" />
              Props
            </h3>
            <div className="flex flex-wrap gap-2">
              {scene.props.map((prop, idx) => (
                <span key={idx} className="chip">{prop}</span>
              ))}
            </div>
          </div>
        )}

        {/* ═══ ACTIONS ═══ */}
        <div className="flex gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} className="btn-secondary text-xs h-9 flex-1" data-testid="save-scene-design-btn">
            {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
            Save Scene Design
          </Button>
          {!design.is_locked && (
            <Button onClick={handleLock} disabled={locking} className="btn-primary text-xs h-9" data-testid="lock-scene-design-btn">
              <Lock size={13} className="mr-1" />Lock Scene
            </Button>
          )}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!showLightbox} onOpenChange={() => setShowLightbox(null)}>
        <DialogContent className="bg-black/95 border-[var(--border-subtle)] max-w-3xl p-2">
          {showLightbox && (
            <div className="relative">
              <img src={`data:image/png;base64,${showLightbox.image_data}`} alt={showLightbox.label || "Preview"} className="w-full rounded-lg" />
              {showLightbox.label && <p className="text-center text-sm text-white/60 mt-2">{showLightbox.label}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ScrollArea>
  );
}
